import { useCallback, useContext, useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { getMemberDetails, MemberDetails } from '@/lib/member-directory'
import { AuthContext } from '@/context/authContext'

export function useGetMemberDetails() {
  const { user } = useContext(AuthContext)
  const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchMemberDetails = useCallback(async () => {
    try {
      setError(null)
      setMemberDetails(null)
      setIsLoading(true)

      if (!user) {
        setError('User not authenticated')
        return
      }

      const idToken = await user.getIdToken()
      const details = await getMemberDetails(idToken)
      setMemberDetails(details)
      setIsLoading(false)
    } catch (e) {
      Sentry.captureMessage('Error in useGetMemberDetails while fetching member details', {
        extra: {
          firebaseUID: user?.uid || 'unknown',
          error: e,
        },
      })
      setError('Error retrieving member details. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchMemberDetails()
  }, [user, fetchMemberDetails])

  return { memberDetails, isLoading, error }
}
